import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  FilmStrip, ArrowsClockwise, Copy, CheckCircle, WarningCircle,
  Megaphone, Users, EnvelopeSimple, FileText, CaretRight, Info, Clock, ArrowCounterClockwise, Trash
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShareButton } from '../components/ShareButton';
import { TextHistoryPanel } from '../components/TextHistoryPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'ugc-script', label: 'UGC Script', icon: FilmStrip, desc: 'Video scripts for UGC creators' },
  { id: 'creator-brief', label: 'Creator Brief', icon: FileText, desc: 'Briefs for external UGC creators' },
  { id: 'influencer-brief', label: 'Influencer Brief', icon: Users, desc: 'Campaign briefs for influencers' },
  { id: 'outreach', label: 'Outreach Email', icon: EnvelopeSimple, desc: 'Personalised influencer outreach' },
];

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} data-testid="copy-result-btn" className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs transition-colors" style={{ background: copied ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.05)', color: copied ? '#c8f060' : 'rgba(245,240,232,0.4)', border: '1px solid ' + (copied ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.07)') }}>
      {copied ? <><CheckCircle size={12} />Copied</> : <><Copy size={12} />Copy</>}
    </button>
  );
};

const DisclosureBanner = ({ text }) => (
  <div className="flex items-start gap-2 p-3 rounded-xl mt-4" data-testid="ftc-disclosure" style={{ background: 'rgba(234,179,8,0.05)', border: '1px solid rgba(234,179,8,0.15)' }}>
    <Info size={16} className="shrink-0 mt-0.5" style={{ color: '#eab308' }} />
    <p className="text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.6)' }}>{text}</p>
  </div>
);

const ProfileWarning = ({ detail }) => (
  <div className="flex items-start gap-2 p-4 rounded-xl" data-testid="profile-warning" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)' }}>
    <WarningCircle size={18} className="shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
    <div>
      <p className="text-sm font-medium mb-1" style={{ color: '#ef4444' }}>Brand Profile Required</p>
      <p className="text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.5)' }}>{detail}</p>
      <a href="/dashboard/settings" className="inline-flex items-center gap-1 mt-2 text-xs font-medium transition-colors" style={{ color: '#c8f060' }}>
        Go to Settings <CaretRight size={12} />
      </a>
    </div>
  </div>
);

const ResultSection = ({ title, content }) => {
  if (!content) return null;
  if (Array.isArray(content)) {
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.4)' }}>{title}</h4>
          <CopyButton text={content.join('\n')} />
        </div>
        <div className="space-y-1.5">
          {content.map((item, i) => (
            <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <span className="text-xs shrink-0 font-medium" style={{ color: '#c8f060' }}>{i + 1}.</span>
              <p className="text-sm" style={{ color: 'rgba(245,240,232,0.7)' }}>{typeof item === 'string' ? item : JSON.stringify(item)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (typeof content === 'object') {
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.4)' }}>{title}</h4>
          <CopyButton text={content} />
        </div>
        {Object.entries(content).map(([key, val]) => (
          <div key={key} className="mb-2">
            <p className="text-xs font-medium mb-1" style={{ color: 'rgba(245,240,232,0.5)' }}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
            {Array.isArray(val) ? (
              <ul className="space-y-1 pl-3">
                {val.map((v, i) => <li key={i} className="text-sm list-disc" style={{ color: 'rgba(245,240,232,0.6)' }}>{typeof v === 'string' ? v : JSON.stringify(v)}</li>)}
              </ul>
            ) : (
              <p className="text-sm" style={{ color: 'rgba(245,240,232,0.6)' }}>{typeof val === 'string' ? val : JSON.stringify(val)}</p>
            )}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.4)' }}>{title}</h4>
        <CopyButton text={content} />
      </div>
      <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
        <p className="text-sm whitespace-pre-wrap" style={{ color: 'rgba(245,240,232,0.7)' }}>{content}</p>
      </div>
    </div>
  );
};

/* ─── UGC Script Tab ─── */
const UGCScriptTab = () => {
  const [product, setProduct] = useState('');
  const [coreBenefit, setCoreBenefit] = useState('');
  const [audience, setAudience] = useState('');
  const [duration, setDuration] = useState('30');
  const [platform, setPlatform] = useState('tiktok');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [profileError, setProfileError] = useState('');

  const generate = async () => {
    setError(''); setProfileError(''); setGenerating(true); setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/ugc/generate-script`, {
        product, core_benefit: coreBenefit, target_audience: audience,
        duration: Number(duration), platform,
      });
      setResult(data);
    } catch (err) {
      if (err.response?.status === 422) setProfileError(err.response?.data?.detail);
      else { const d = err.response?.data?.detail; setError(typeof d === "string" ? d : (d?.message || "Generation failed.")); }
    } finally { setGenerating(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5 space-y-4">
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Product / Brand *</label>
            <input value={product} onChange={e => setProduct(e.target.value)} data-testid="ugc-product-input" placeholder="Your product name (auto-fills from brand profile)" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Core Benefit</label>
            <input value={coreBenefit} onChange={e => setCoreBenefit(e.target.value)} data-testid="ugc-benefit-input" placeholder="e.g., saves 3 hours per week" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Target Audience</label>
            <input value={audience} onChange={e => setAudience(e.target.value)} data-testid="ugc-audience-input" placeholder="e.g., busy moms, SaaS founders" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Duration</label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger data-testid="ugc-duration-select" className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {['15', '30', '60'].map(d => <SelectItem key={d} value={d} className="text-white hover:bg-white/10 text-sm">{d}s</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Platform</label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="ugc-platform-select" className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {[['tiktok','TikTok'],['instagram','Instagram'],['youtube','YouTube Shorts'],['facebook','Facebook']].map(([v,l]) => <SelectItem key={v} value={v} className="text-white hover:bg-white/10 text-sm">{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {profileError && <ProfileWarning detail={profileError} />}
          {error && <div data-testid="ugc-error" className="text-sm rounded-xl px-4 py-2 flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400"><WarningCircle size={16} />{error}</div>}
          <button onClick={generate} disabled={generating} data-testid="generate-ugc-script-btn" className="w-full py-3 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50" style={{ background: '#c8f060', color: '#080808' }}>
            {generating ? <><ArrowsClockwise size={16} className="animate-spin" />Writing your script…</> : <><FilmStrip size={16} />Create UGC Script</>}
          </button>
          <p className="text-center text-[10px]" style={{ color: 'rgba(245,240,232,0.25)' }}>Free — uses daily AI generation limit</p>
        </div>
      </div>
      <div className="lg:col-span-3">
        {result ? (
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5" data-testid="ugc-script-result">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>Your UGC Script</h3>
              <div className="flex items-center gap-2">
                <ShareButton title="UGC Script" content={result.body_script || result.raw_script || ''} type="UGC Script" />
                <CopyButton text={result} />
              </div>
            </div>
            <ResultSection title="Hook Options" content={result.hook_options} />
            <ResultSection title="Body Script" content={result.body_script} />
            <ResultSection title="Delivery Notes" content={result.delivery_notes} />
            <ResultSection title="B-Roll Suggestions" content={result.broll_suggestions} />
            <ResultSection title="CTA Options" content={result.cta_options} />
            {result.raw_script && <ResultSection title="Script" content={result.raw_script} />}
            <DisclosureBanner text={result.disclosure} />
          </div>
        ) : (
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 text-center py-20">
            <FilmStrip size={40} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.1)' }} />
            <h3 className="font-medium mb-1" style={{ color: 'rgba(245,240,232,0.5)', fontFamily: "'Instrument Serif', serif" }}>Create a UGC Script</h3>
            <p className="text-xs" style={{ color: 'rgba(245,240,232,0.25)' }}>Fill in your product details and get a word-for-word creator script with hooks, delivery notes, and B-roll ideas</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Creator Brief Tab ─── */
const CreatorBriefTab = () => {
  const [brandName, setBrandName] = useState('');
  const [product, setProduct] = useState('');
  const [goal, setGoal] = useState('awareness and conversions');
  const [audience, setAudience] = useState('');
  const [platform, setPlatform] = useState('TikTok and Instagram');
  const [style, setStyle] = useState('authentic and casual');
  const [budget, setBudget] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [profileError, setProfileError] = useState('');

  const generate = async () => {
    setError(''); setProfileError(''); setGenerating(true); setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/ugc/generate-creator-brief`, {
        brand_name: brandName, product, campaign_goal: goal,
        target_audience: audience, platform, style, budget,
      });
      setResult(data);
    } catch (err) {
      if (err.response?.status === 422) setProfileError(err.response?.data?.detail);
      else { const d = err.response?.data?.detail; setError(typeof d === "string" ? d : (d?.message || "Generation failed.")); }
    } finally { setGenerating(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5 space-y-4">
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Brand Name</label>
            <input value={brandName} onChange={e => setBrandName(e.target.value)} data-testid="brief-brand-input" placeholder="Auto-fills from brand profile" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Product</label>
            <input value={product} onChange={e => setProduct(e.target.value)} data-testid="brief-product-input" placeholder="Product being featured" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Campaign Goal</label>
            <Select value={goal} onValueChange={setGoal}>
              <SelectTrigger className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {['awareness and conversions', 'brand awareness', 'direct sales', 'app installs', 'lead generation'].map(g => <SelectItem key={g} value={g} className="text-white hover:bg-white/10 text-sm capitalize">{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Target Audience</label>
            <input value={audience} onChange={e => setAudience(e.target.value)} data-testid="brief-audience-input" placeholder="e.g., Women 25-34 interested in wellness" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Platform</label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {['TikTok and Instagram', 'TikTok', 'Instagram', 'YouTube', 'Facebook'].map(p => <SelectItem key={p} value={p} className="text-white hover:bg-white/10 text-sm">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Style</label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {['authentic and casual', 'polished editorial', 'high energy', 'cinematic', 'funny and relatable'].map(s => <SelectItem key={s} value={s} className="text-white hover:bg-white/10 text-sm capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Budget per Creator</label>
            <input value={budget} onChange={e => setBudget(e.target.value)} data-testid="brief-budget-input" placeholder="e.g., $200-500" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          {profileError && <ProfileWarning detail={profileError} />}
          {error && <div className="text-sm rounded-xl px-4 py-2 flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400"><WarningCircle size={16} />{error}</div>}
          <button onClick={generate} disabled={generating} data-testid="generate-creator-brief-btn" className="w-full py-3 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50" style={{ background: '#c8f060', color: '#080808' }}>
            {generating ? <><ArrowsClockwise size={16} className="animate-spin" />Writing your brief…</> : <><FileText size={16} />Create Creator Brief</>}
          </button>
          <p className="text-center text-[10px]" style={{ color: 'rgba(245,240,232,0.25)' }}>Free — uses daily AI generation limit</p>
        </div>
      </div>
      <div className="lg:col-span-3">
        {result ? (
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5" data-testid="creator-brief-result">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>Creator Brief</h3>
              <CopyButton text={result} />
            </div>
            <ResultSection title="Campaign Overview" content={result.campaign_overview} />
            <ResultSection title="Talking Points" content={result.talking_points} />
            <ResultSection title="Do's and Don'ts" content={result.dos_and_donts} />
            <ResultSection title="Visual References" content={result.visual_references} />
            <ResultSection title="Deliverables" content={result.deliverables} />
            <ResultSection title="Usage Rights" content={result.usage_rights} />
            {result.raw_brief && <ResultSection title="Brief" content={result.raw_brief} />}
            <DisclosureBanner text={result.disclosure} />
          </div>
        ) : (
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 text-center py-20">
            <FileText size={40} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.1)' }} />
            <h3 className="font-medium mb-1" style={{ color: 'rgba(245,240,232,0.5)', fontFamily: "'Instrument Serif', serif" }}>Create a Creator Brief</h3>
            <p className="text-xs" style={{ color: 'rgba(245,240,232,0.25)' }}>Create professional briefs to send to UGC creators with dos/donts, talking points, and deliverable specs</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Influencer Brief Tab ─── */
const InfluencerBriefTab = () => {
  const [brandName, setBrandName] = useState('');
  const [product, setProduct] = useState('');
  const [objective, setObjective] = useState('awareness and sales');
  const [audience, setAudience] = useState('');
  const [influencerType, setInfluencerType] = useState('micro-influencer 10K-100K followers');
  const [platform, setPlatform] = useState('Instagram');
  const [contentType, setContentType] = useState('Reel + Story');
  const [budget, setBudget] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [profileError, setProfileError] = useState('');

  const generate = async () => {
    setError(''); setProfileError(''); setGenerating(true); setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/influencer/generate-brief`, {
        brand_name: brandName, product, objective,
        target_audience: audience, influencer_type: influencerType,
        platform, content_type: contentType, budget,
      });
      setResult(data);
    } catch (err) {
      if (err.response?.status === 422) setProfileError(err.response?.data?.detail);
      else { const d = err.response?.data?.detail; setError(typeof d === "string" ? d : (d?.message || "Generation failed.")); }
    } finally { setGenerating(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5 space-y-4">
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Brand Name</label>
            <input value={brandName} onChange={e => setBrandName(e.target.value)} data-testid="inf-brand-input" placeholder="Auto-fills from brand profile" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Product</label>
            <input value={product} onChange={e => setProduct(e.target.value)} data-testid="inf-product-input" placeholder="Product being promoted" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Objective</label>
              <Select value={objective} onValueChange={setObjective}>
                <SelectTrigger className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {['awareness and sales', 'brand awareness', 'direct sales', 'app installs', 'community growth'].map(o => <SelectItem key={o} value={o} className="text-white hover:bg-white/10 text-sm capitalize">{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Influencer Type</label>
              <Select value={influencerType} onValueChange={setInfluencerType}>
                <SelectTrigger className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {['nano 1K-10K followers', 'micro-influencer 10K-100K followers', 'mid-tier 100K-500K followers', 'macro 500K-1M followers', 'mega 1M+ followers'].map(t => <SelectItem key={t} value={t} className="text-white hover:bg-white/10 text-sm capitalize">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Target Audience</label>
            <input value={audience} onChange={e => setAudience(e.target.value)} data-testid="inf-audience-input" placeholder="e.g., Gen Z beauty enthusiasts" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Platform</label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {['Instagram', 'TikTok', 'YouTube', 'Twitter/X'].map(p => <SelectItem key={p} value={p} className="text-white hover:bg-white/10 text-sm">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Content Type</label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {['Reel + Story', 'Feed Post + Story', 'Video Review', 'Story Only', 'Live Stream'].map(c => <SelectItem key={c} value={c} className="text-white hover:bg-white/10 text-sm">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Budget</label>
            <input value={budget} onChange={e => setBudget(e.target.value)} data-testid="inf-budget-input" placeholder="e.g., $500-2000 per post" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          {profileError && <ProfileWarning detail={profileError} />}
          {error && <div className="text-sm rounded-xl px-4 py-2 flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400"><WarningCircle size={16} />{error}</div>}
          <button onClick={generate} disabled={generating} data-testid="generate-influencer-brief-btn" className="w-full py-3 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50" style={{ background: '#c8f060', color: '#080808' }}>
            {generating ? <><ArrowsClockwise size={16} className="animate-spin" />Writing your brief…</> : <><Users size={16} />Create Influencer Brief</>}
          </button>
          <p className="text-center text-[10px]" style={{ color: 'rgba(245,240,232,0.25)' }}>Free — uses daily AI generation limit</p>
        </div>
      </div>
      <div className="lg:col-span-3">
        {result ? (
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5" data-testid="influencer-brief-result">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>Influencer Campaign Brief</h3>
              <CopyButton text={result} />
            </div>
            <ResultSection title="Campaign Overview" content={result.campaign_overview} />
            <ResultSection title="Influencer Fit" content={result.influencer_fit} />
            <ResultSection title="Content Requirements" content={result.content_requirements} />
            <ResultSection title="Key Messages" content={result.key_messages} />
            <ResultSection title="Creative Freedom" content={result.creative_freedom} />
            <ResultSection title="Do's and Don'ts" content={result.dos_and_donts} />
            <ResultSection title="Compensation" content={result.compensation} />
            <ResultSection title="Hashtags & Tags" content={result.hashtags_and_tags} />
            <ResultSection title="Approval Process" content={result.approval_process} />
            <ResultSection title="Legal Disclosure" content={result.legal_disclosure} />
            {result.raw_brief && <ResultSection title="Brief" content={result.raw_brief} />}
            <DisclosureBanner text={result.disclosure} />
          </div>
        ) : (
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 text-center py-20">
            <Users size={40} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.1)' }} />
            <h3 className="font-medium mb-1" style={{ color: 'rgba(245,240,232,0.5)', fontFamily: "'Instrument Serif', serif" }}>Create an Influencer Brief</h3>
            <p className="text-xs" style={{ color: 'rgba(245,240,232,0.25)' }}>Create comprehensive campaign briefs with deliverables, compensation terms, and FTC-compliant disclosure language</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Outreach Email Tab ─── */
const OutreachEmailTab = () => {
  const [brandName, setBrandName] = useState('');
  const [product, setProduct] = useState('');
  const [influencerNiche, setInfluencerNiche] = useState('');
  const [influencerName, setInfluencerName] = useState('');
  const [collabType, setCollabType] = useState('gifted + affiliate');
  const [offerDetails, setOfferDetails] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [profileError, setProfileError] = useState('');

  const generate = async () => {
    setError(''); setProfileError(''); setGenerating(true); setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/influencer/generate-outreach`, {
        brand_name: brandName, product, influencer_niche: influencerNiche,
        influencer_name: influencerName, collab_type: collabType,
        offer_details: offerDetails,
      });
      setResult(data);
    } catch (err) {
      if (err.response?.status === 422) setProfileError(err.response?.data?.detail);
      else { const d = err.response?.data?.detail; setError(typeof d === "string" ? d : (d?.message || "Generation failed.")); }
    } finally { setGenerating(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5 space-y-4">
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Brand Name</label>
            <input value={brandName} onChange={e => setBrandName(e.target.value)} data-testid="outreach-brand-input" placeholder="Auto-fills from brand profile" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Product</label>
            <input value={product} onChange={e => setProduct(e.target.value)} data-testid="outreach-product-input" placeholder="Product being pitched" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Influencer Name</label>
            <input value={influencerName} onChange={e => setInfluencerName(e.target.value)} data-testid="outreach-name-input" placeholder="e.g., Sarah Johnson (or leave blank)" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Influencer Niche</label>
            <input value={influencerNiche} onChange={e => setInfluencerNiche(e.target.value)} data-testid="outreach-niche-input" placeholder="e.g., beauty, fitness, tech reviews" className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30" />
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Collaboration Type</label>
            <Select value={collabType} onValueChange={setCollabType}>
              <SelectTrigger className="bg-zinc-900 border-white/10 text-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {['gifted + affiliate', 'paid partnership', 'gifted only', 'affiliate only', 'brand ambassador'].map(t => <SelectItem key={t} value={t} className="text-white hover:bg-white/10 text-sm capitalize">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Offer Details</label>
            <textarea value={offerDetails} onChange={e => setOfferDetails(e.target.value)} data-testid="outreach-offer-input" placeholder="e.g., Free product + 15% affiliate commission" rows={2} className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-600 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-white/30 resize-none" />
          </div>
          {profileError && <ProfileWarning detail={profileError} />}
          {error && <div className="text-sm rounded-xl px-4 py-2 flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400"><WarningCircle size={16} />{error}</div>}
          <button onClick={generate} disabled={generating} data-testid="generate-outreach-btn" className="w-full py-3 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50" style={{ background: '#c8f060', color: '#080808' }}>
            {generating ? <><ArrowsClockwise size={16} className="animate-spin" />Writing your email…</> : <><EnvelopeSimple size={16} />Create Outreach Email</>}
          </button>
          <p className="text-center text-[10px]" style={{ color: 'rgba(245,240,232,0.25)' }}>Free — uses daily AI generation limit</p>
        </div>
      </div>
      <div className="lg:col-span-3">
        {result ? (
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-5" data-testid="outreach-result">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>Outreach Email</h3>
              <CopyButton text={result} />
            </div>
            <ResultSection title="Subject Line Options" content={result.subject_line_options} />
            <ResultSection title="Email Body" content={result.email_body} />
            <ResultSection title="Follow-Up Email" content={result.follow_up_email} />
            {result.raw_outreach && <ResultSection title="Email" content={result.raw_outreach} />}
            <DisclosureBanner text={result.disclosure} />
          </div>
        ) : (
          <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 text-center py-20">
            <EnvelopeSimple size={40} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.1)' }} />
            <h3 className="font-medium mb-1" style={{ color: 'rgba(245,240,232,0.5)', fontFamily: "'Instrument Serif', serif" }}>Create Outreach Emails</h3>
            <p className="text-xs" style={{ color: 'rgba(245,240,232,0.25)' }}>Create personalised outreach emails that actually get read — subject lines, body copy, and follow-ups</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main Page ─── */
export default function CreatorInfluencerPage() {
  const [activeTab, setActiveTab] = useState('ugc-script');
  const [pageTab, setPageTab] = useState('create');

  const TabContent = {
    'ugc-script': UGCScriptTab,
    'creator-brief': CreatorBriefTab,
    'influencer-brief': InfluencerBriefTab,
    'outreach': OutreachEmailTab,
  };

  const ActiveComponent = TabContent[activeTab];

  const handleHistoryReload = (item) => {
    // Switch to create tab
    setPageTab('create');
  };

  return (
    <DashboardLayout>
      <div data-testid="creator-influencer-page" className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.08)', border: '1px solid rgba(200,240,96,0.15)' }}>
              <Megaphone size={20} style={{ color: '#c8f060' }} />
            </div>
            <div>
              <h1 className="text-2xl font-medium" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>Creator & Influencer Ads</h1>
              <p className="text-sm" style={{ color: 'rgba(245,240,232,0.35)' }}>Scripts, briefs, and outreach for UGC and influencer campaigns</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setPageTab(t)} data-testid={`page-tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: pageTab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${pageTab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: pageTab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Create' : 'History'}
              </button>
            ))}
          </div>
        </motion.div>

        {pageTab === 'create' && (
        <>
        {/* Tab Navigation */}
        <div className="flex gap-1 p-1 rounded-xl overflow-x-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all"
                style={{
                  background: isActive ? 'rgba(200,240,96,0.1)' : 'transparent',
                  color: isActive ? '#c8f060' : 'rgba(245,240,232,0.4)',
                  border: isActive ? '1px solid rgba(200,240,96,0.15)' : '1px solid transparent',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Active Tab Content */}
        <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
          <ActiveComponent />
        </motion.div>
        </>
        )}

        {/* History Tab */}
        {pageTab === 'history' && (
          <div className="space-y-6">
            <div className="rounded-[14px] p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h2 className="font-medium mb-4" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>UGC & Creator Brief History</h2>
              <TextHistoryPanel toolType="ugc_script" onReload={handleHistoryReload} />
            </div>
            <div className="rounded-[14px] p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
              <h2 className="font-medium mb-4" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>Influencer Brief & Outreach History</h2>
              <TextHistoryPanel toolType="influencer_brief" onReload={handleHistoryReload} />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
